import React, { createContext, useContext, useMemo, type ReactNode } from 'react';

interface PlanContextValue {
  plan: string;
  enabledFeatures: string[];
  isStandard: boolean;
  isEnterprise: boolean;
  isFeatureEnabled: (key: string) => boolean;
}

const PlanContext = createContext<PlanContextValue | null>(null);

interface PlanProviderProps {
  plan?: string;
  enabledFeatures?: string[];
  children: ReactNode;
}

/**
 * PlanProvider — Organization 層級的 plan / feature gate context。
 * admin 和 operator 兩端都應包裹此 Provider，各自傳入對應的 plan 資料。
 */
export function PlanProvider({ plan: planProp, enabledFeatures: featuresProp, children }: PlanProviderProps) {
  const value = useMemo<PlanContextValue>(() => {
    const plan = planProp || 'standard';
    const enabledFeatures = featuresProp || [];
    const isStandard = plan === 'standard';
    const isEnterprise = plan === 'enterprise';

    return {
      plan,
      enabledFeatures,
      isStandard,
      isEnterprise,
      isFeatureEnabled: (key: string) => enabledFeatures.includes(key),
    };
  }, [planProp, featuresProp]);

  return <PlanContext.Provider value={value}>{children}</PlanContext.Provider>;
}

export function usePlan(): PlanContextValue {
  const ctx = useContext(PlanContext);
  if (!ctx) throw new Error('usePlan must be used within PlanProvider');
  return ctx;
}

export const useFeatureEnabled = (key: string): boolean => usePlan().isFeatureEnabled(key);
export const useIsStandard = (): boolean => usePlan().isStandard;
export const useIsEnterprise = (): boolean => usePlan().isEnterprise;

export default PlanContext;
